import { useState, useCallback } from "react";
import { ethers } from "ethers";
import axios from "axios";
import {
  APPROVE_BUILDER_FEE_TYPES,
} from "../config/builderFee";

export function useBuilderFeeApproval() {
  const [state, setState] = useState({
    loading: false,
    error: null,
    approved: false,
  });

  const checkStatus = useCallback(async () => {
    try {
      const { data } = await axios.get("/api/builder-fee/status");
      setState((s) => ({ ...s, approved: data.approved }));
      return data.approved;
    } catch {
      return false;
    }
  }, []);

  // signer and network must come from the wallet context (useWallet()) — never
  // construct a fresh Web3Provider(window.ethereum) here, as that triggers an
  // eth_accounts call that throws MetaMask error 4100 after wallet_revokePermissions.
  const approve = useCallback(
    async (signer, network, builderAddress, maxFeeRate, hyperliquidNetwork, portfolioId) => {
      setState({ loading: true, error: null, approved: false });

      try {
        // 1. Use the caller's already-authenticated signer from WalletContext.
        if (!signer) {
          throw new Error("Wallet not connected. Please connect MetaMask first.");
        }

        // 2. Derive chainId from the network already known by the wallet context.
        const walletNetwork = network ?? await signer.provider.getNetwork();
        const walletChainId = walletNetwork.chainId;
        const signatureChainId = '0x' + walletChainId.toString(16);

        // 3. Build EIP-712 domain with the actual wallet chain
        const domain = {
          name: 'HyperliquidSignTransaction',
          version: '1',
          chainId: walletChainId,
          verifyingContract: '0x0000000000000000000000000000000000000000',
        };

        // 4. Build the action payload
        const nonce = Date.now();
        const action = {
          type: "approveBuilderFee",
          hyperliquidChain: hyperliquidNetwork,
          signatureChainId,
          maxFeeRate,
          builder: builderAddress,
          nonce,
        };

        // 5. Construct EIP-712 message (only fields in the types, not `type` or `signatureChainId`)
        const message = {
          hyperliquidChain: action.hyperliquidChain,
          maxFeeRate: action.maxFeeRate,
          builder: action.builder,
          nonce: action.nonce,
        };

        // 6. Sign with MetaMask via EIP-712
        const signature = await signer._signTypedData(
          domain,
          APPROVE_BUILDER_FEE_TYPES,
          message
        );

        // 7. Split signature into r, s, v
        const { r, s, v } = ethers.utils.splitSignature(signature);

        // 8. Send to our API which relays to Hyperliquid
        await axios.post("/api/builder-fee/approve", {
          action,
          nonce,
          signature: { r, s, v },
          ...(portfolioId != null && { portfolio_id: portfolioId }),
        });

        setState({ loading: false, error: null, approved: true });
        return true;
      } catch (err) {
        // MetaMask 4100: site permission was revoked — force a clean logout so
        // the user is prompted to reconnect rather than seeing a cryptic error.
        if (err?.code === 4100 || err?.error?.code === 4100) {
          window.dispatchEvent(new CustomEvent('sessionExpired', {
            detail: { reason: 'metamask-unauthorized' }
          }));
        }
        const msg =
          err?.response?.data?.detail ||
          err?.message ||
          "Failed to approve builder fee";
        setState({ loading: false, error: msg, approved: false });
        return false;
      }
    },
    []
  );

  return { ...state, approve, checkStatus };
}
